/**
 * @fileoverview Modal Dialog Components
 *
 * `ModalDialog` is a custom-element modal with focus trapping, body scroll
 * locking, and click/Escape-to-close. `ModalOpener` triggers a target modal,
 * either via an internal button or by self-click when `data-self-open="true"`.
 *
 * THESE COMPONENTS SHOULD BE DEPRECATED OVER TIME AS WE ADOPT NATIVE HTML
 * DIALOG AND POPOVER FEATURES.
 *
 * @example
 * <modal-opener data-modal="#my-modal"><button>Open</button></modal-opener>
 * <modal-dialog id="my-modal">…</modal-dialog>
 */

import { trapFocus, removeTrapFocus, pauseAllMedia } from '@utilities/theme';

/**
 * @classdesc Accessible modal dialog custom element.
 * @extends HTMLElement
 */
export class ModalDialog extends HTMLElement {
  constructor() {
    super();
    this.querySelector('[id^="ModalClose-"]').addEventListener(
      'click',
      this.hide.bind(this)
    );
    this.querySelector('.modal-overlay')?.addEventListener(
      'click',
      this.hide.bind(this)
    );
    this.addEventListener('keyup', (event) => {
      if (event.code?.toUpperCase() === 'ESCAPE') this.hide();
    });
    if (this.classList.contains('media-modal')) {
      this.addEventListener('pointerup', (event) => {
        if (event.pointerType === 'mouse' && !event.target.closest('deferred-media, product-model')) this.hide();
      });
    } else {
      this.addEventListener('click', (event) => {
        if (event.target.nodeName === 'MODAL-DIALOG') this.hide();
      });
    }
  }

  connectedCallback() {
    if (this.moved) return;
    this.moved = true;

    if(this.classList.contains('quick-add-modal')) {
      document.querySelector('#main-content').appendChild(this);
    } else {
      document.body.appendChild(this);
    }

    this.addEventListener('click', (event) => {
      if (event.target.nodeName === 'MODAL-DIALOG') this.hide();
    });
  }

  show(opener) {
    this.openedBy = opener;
    const popup = this.querySelector('.template-popup');
    document.body.style.overflow = 'hidden';
    this.setAttribute('open', '');
    if (popup) popup.loadContent();
    trapFocus(this, this.querySelector('[role="dialog"]'));
    pauseAllMedia();
    // If the modal opening contains a bundle form, we want to generate the bundle id and attach it.
    const bundleForm = this.querySelector('welcome-package-form');
    if(bundleForm){
      // our best attempt to get a good 'random' number for the bundle id
      let bundleId = Math.floor(Math.random() * Math.floor(Math.random() * Date.now()));
      bundleForm.dataset.bundleId = `bundle-${bundleId}`;
    }
  }

  hide() {
    document.body.style.overflow = 'auto';
    this.removeAttribute('open');
    removeTrapFocus(this.openedBy);
    pauseAllMedia();
    const backInStockForm = this.querySelector('.js-back-in-stock');
    if(backInStockForm){
      backInStockForm.classList.remove('product__stock--success');
      backInStockForm.classList.remove('product__stock--error');
    }
  }
}

if (!customElements.get('modal-dialog')) {
  customElements.define('modal-dialog', ModalDialog);
}

/**
 * @classdesc Opens a target `ModalDialog`, via an internal button or self-click.
 * @extends HTMLElement
 */
export class ModalOpener extends HTMLElement {
  constructor() {
    super();
    this.boundHandleModalOpen = this.handleModalOpen.bind(this);
    this.initialized = false;
  }

  connectedCallback() {
    // Defer initialization to allow innerHTML to be set after createElement
    // Use requestAnimationFrame to ensure DOM is fully ready
    requestAnimationFrame(() => this.initialize());
  }

  initialize() {
    if (this.initialized) return;
    this.initialized = true;

    // Check if this component should open modals when clicked directly
    const isSelfOpening = this.dataset.selfOpen === 'true';

    if (isSelfOpening) {
      // Register click event on the component itself
      this.addEventListener('click', this.boundHandleModalOpen);
    } else {
      // Look for a button element to handle clicks (existing behavior)
      const button = this.querySelector('button');

      if (!button) return;
      button.addEventListener('click', this.boundHandleModalOpen);
    }
  }

  /**
   * Handles the modal opening logic
   * @param {Event} event - The click event
   * @private
   */
  handleModalOpen(event) {
    const dataModal = this.getAttribute('data-modal');
    const modal = document.querySelector(dataModal);

    if (modal) {
      // For self-opening modals, pass the component itself as the opener
      // For button-based modals, pass the button element
      const opener = this.dataset.selfOpen === 'true' ? this : event.currentTarget;

      modal.show(opener);

      // Special handling for size modals
      if (dataModal.includes("size")) {
        const sizeContainer = document.querySelector(".product__size-inner");
        if (sizeContainer && sizeContainer.scrollWidth <= sizeContainer.clientWidth) {
          const slideOption = document.querySelector(".slide-option");
          if (slideOption) {
            slideOption.style.display = "none";
          }
        }
      }
    }
  }
}

if (!customElements.get('modal-opener')) {
  customElements.define('modal-opener', ModalOpener);
}
